import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import MainHeader from "../../components/header/MainHeader";
import { LinkPreview } from "@dhaiwat10/react-link-preview";
import {
  useDeletePreviousExperienceMutation,
  useGetProfileDetailsQuery,
  useGetProfileStatsQuery,
  useRegisterProfileVisitQuery,
} from "../../services/profileAPI";
import { useRouter } from "next/router";
import { format } from "date-fns";
import FullScreenLoader from "../../components/common/FullScreenLoader";
import ErrorState from "../../components/common/ErrorState";
import ChAccordion from "../../components/profile/ChAccordion";
import styles from "../../../styles/profile.module.css";
import ProfileCard from "../../components/profile/ProfileCard";
import { useAppSelector } from "../../app/hooks";
import ProfilePrompts from "../../components/profile/ProfilePrompts";
import ProfileIntroCard from "../../components/profile/ProfileIntroCard";
import ProtectedRoute from "../../components/common/ProtectedRoute";
import Head from "next/head";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditPastExperiences from "../../components/profile/EditPastExperiences";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import axios from "axios";

const ProfileScreen = () => {
  const router = useRouter();
  const { type } = router.query;
  const [showAllClubhouses, setShowAllClubhouses] = useState(false);
  const auth = useAppSelector((state) => state.auth);
  const [chosenExperience, setChosenExperience] = useState(null);
  const [editPastExperienceOpen, setEditPastExperienceOpen] = useState(false);
  const [deletePreviousExperience] = useDeletePreviousExperienceMutation();

  const isSameUser = auth.userId === router.query.profileId;

  const {
    data: profileData,
    isLoading,
    isError,
  } = useGetProfileDetailsQuery((router.query.profileId as string) || "");

  useRegisterProfileVisitQuery(
    {
      userId: auth?.userId || "",
      profileId: (router?.query?.profileId as string) || "",
    },
    { skip: auth?.userId === router.query.profileId }
  );

  const { data: profileStats } = useGetProfileStatsQuery(profileData?._id!, {
    skip: !isSameUser,
  });

  console.log("profileStats", profileStats);

  let clubhousesToShow =
    showAllClubhouses && profileData?.chList?.length
      ? profileData?.chList
      : profileData?.chList?.slice(0, 3);

  const showClubhouseHandler = () => {
    setShowAllClubhouses(!showAllClubhouses);
  };

  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const onClose = () => {
    setChosenExperience(null);
    setEditPastExperienceOpen(false);
  };

  const onDeleteExperienceClick = async (experienceId: string) => {
    confirm({
      description: "are you sure you want to delete this experience?",
      title: "delete experience",
      confirmationText: "ok",
      cancellationText: "cancel",
    })
      .then(async () => {
        try {
          await deletePreviousExperience(experienceId).unwrap();
          enqueueSnackbar("updated your experience", {
            variant: "success",
          });
        } catch (error) {
          console.error(error);
          enqueueSnackbar("failed to update your experience", {
            variant: "error",
          });
        }
      })
      .catch(() => {
        onClose();
      });
  };

  const onImpresionsInfoClickHandler = () => {
    confirm({
      description:
        "the impressions includes posts made on the feed and clubhouses",
      title: "impressions",
      confirmationText: "cool",
      cancellationText: "",
    })
      .then(async () => {})
      .catch(() => {});
  };

  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (isError && auth?.userId && auth.userId.length > 0) {
    return <ErrorState text="unable to fetch profile details" />;
  }

  const customFetcher = async (url: string) => {
    const { data } = await axios.get(`/api/get-metadata?url=${url}`);
    return data.metadata;
  };

  return (
    <ProtectedRoute>
      <Head>
        <title>leap.club | {profileData?.name!} profile</title>
      </Head>
      <MainHeader
        showBookmarkIcon={!isSameUser}
        isBookmarked={profileData?.isBookmarked}
        currentUserId={auth?.userId || ""}
        profileId={(router?.query?.profileId as string) || ""}
      />
      <Container maxWidth="lg" sx={{ pb: 4, mt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <ProfileIntroCard
              isSameUser={isSameUser}
              image={profileData?.image!}
              name={profileData?.name!}
              city={profileData?.city!}
              currentCompany={profileData?.currentCompany!}
              onboardingDate={profileData?.onboardingDate!}
              headline={profileData?.headline!}
              connected={profileData?.connected!}
              memberId={profileData?._id!}
              connectionsCount={profileData?.connectionsCount!}
              isVip={profileData?.isVip!}
              type={type as string}
              champion={profileData?.champion || ""}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <ProfileCard
              title="introduction"
              isSameUser={isSameUser}
              from="introduction"
              introduction={profileData?.introduction}
            >
              <Typography
                variant="subtitle2"
                color="secondary"
                fontWeight="400"
                sx={{ whiteSpace: "pre-line" }}
              >
                {profileData?.introduction}
              </Typography>
            </ProfileCard>

            {isSameUser && (
              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  px: 2,
                  py: 1,
                  mb: 2,
                  height: "auto",
                  position: "relative",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography fontWeight="500" variant="subtitle1" gutterBottom>
                    my audience
                  </Typography>
                  <Typography fontWeight="500" variant="caption" gutterBottom>
                    only visible to you
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  py={2}
                >
                  <Stack direction="row" alignItems="center">
                    <Typography fontWeight="700" variant="subtitle2" mr={0.5}>
                      {profileStats?.totalFollowing!}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color="secondary"
                      fontWeight="400"
                      sx={{ whiteSpace: "pre-line" }}
                    >
                      members i follow
                    </Typography>
                  </Stack>
                </Stack>
                <Divider />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  py={2}
                >
                  <Stack direction="row" alignItems="center">
                    <Typography fontWeight="700" variant="subtitle2" mr={0.5}>
                      {profileStats?.followersInLast30days}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color="secondary"
                      fontWeight="400"
                      sx={{ whiteSpace: "pre-line" }}
                    >
                      members followed me
                    </Typography>
                  </Stack>
                  <Typography fontWeight="400" variant="caption">
                    in the last 30 days
                  </Typography>
                </Stack>
                <Divider />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  py={2}
                >
                  <Stack direction="row" alignItems="center">
                    <Typography fontWeight="700" variant="subtitle2" mr={0.5}>
                      {profileStats?.profilePageVisitsInLast30days}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color="secondary"
                      fontWeight="400"
                      sx={{ whiteSpace: "pre-line" }}
                    >
                      viewed my profile
                    </Typography>
                  </Stack>
                  <Typography fontWeight="400" variant="caption">
                    in the last 30 days
                  </Typography>
                </Stack>
                <Divider />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  py={2}
                >
                  <Stack direction="row" alignItems="center">
                    <Typography fontWeight="700" variant="subtitle2" mr={0.5}>
                      {profileStats?.postImpressions}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color="secondary"
                      fontWeight="400"
                      sx={{ whiteSpace: "pre-line" }}
                    >
                      impressions on my post
                    </Typography>
                    <IconButton onClick={onImpresionsInfoClickHandler}>
                      <InfoOutlinedIcon sx={{ fontSize: "16px" }} />
                    </IconButton>
                  </Stack>
                  <Typography fontWeight="400" variant="caption">
                    in the last 30 days
                  </Typography>
                </Stack>
              </Paper>
            )}

            <ProfileCard title="know me better">
              <ProfilePrompts
                prompts={profileData?.prompts!}
                isSameUser={isSameUser}
                name={profileData?.name!}
                userId={profileData?._id || ""}
                hasPoked={profileData?.promptNudgeStatus || false}
                pokeCount={profileData?.promptNudgeCount || 0}
              />
            </ProfileCard>

            <ProfileCard
              title="my social interests and hobbies"
              isSameUser={isSameUser}
              from="hobbies"
              hobbies={profileData?.hobbies}
            >
              <Stack direction="row" flexWrap="wrap">
                {profileData?.hobbies?.map((hobby: string, index) => (
                  <Typography
                    key={index}
                    variant="subtitle2"
                    color="secondary"
                    fontWeight="400"
                  >
                    {hobby}
                    {profileData?.hobbies?.length - 1 === index ? "." : ","}
                    &nbsp;
                  </Typography>
                ))}
              </Stack>
            </ProfileCard>

            <ProfileCard
              title="my hero"
              isSameUser={isSameUser}
              from="hero"
              hero={profileData?.leader}
            >
              <Typography
                variant="subtitle2"
                color="secondary"
                fontWeight="400"
              >
                {profileData?.leader}
              </Typography>
            </ProfileCard>

            <ProfileCard
              title="my goals"
              isSameUser={isSameUser}
              from="goals"
              goals={profileData?.goals}
            >
              <Stack direction="row" flexWrap="wrap">
                {profileData?.goals?.map((goal: string, index) => (
                  <Typography
                    key={index}
                    variant="subtitle2"
                    color="secondary"
                    fontWeight="400"
                  >
                    {goal}
                    {profileData?.goals?.length - 1 === index ? "." : ","}&nbsp;
                  </Typography>
                ))}
              </Stack>
            </ProfileCard>

            <Paper
              elevation={0}
              variant="outlined"
              sx={{ px: 2, py: 1, mb: 2 }}
            >
              <ChAccordion
                clubhousesToShow={clubhousesToShow!}
                showClubhouseHandler={showClubhouseHandler}
                showArrow={profileData?.chList?.length! > 3}
              />
            </Paper>

            <ProfileCard
              title="companies i admire"
              isSameUser={isSameUser}
              from="admiredCompanies"
              admiredCompanies={profileData?.companyAdmire}
            >
              <Stack direction="row" flexWrap="wrap" mt={1}>
                {profileData?.companyAdmire.map((company) => (
                  <Chip
                    key={company}
                    label={company}
                    variant="outlined"
                    size="small"
                    sx={{
                      fontSize: 12,
                      fontWeight: 400,
                      borderRadius: "4px",
                      mr: 1,
                      mb: 1,
                      color: "secondary",
                    }}
                  />
                ))}
              </Stack>
            </ProfileCard>

            <ProfileCard
              title="years of experience"
              isSameUser={isSameUser}
              from="experience"
              experience={profileData?.experience}
            >
              <Typography
                variant="subtitle2"
                color="secondary"
                fontWeight="400"
              >
                {profileData?.experience}
              </Typography>
            </ProfileCard>

            <ProfileCard
              title="past work experience"
              isSameUser={isSameUser}
              from="pastExperience"
            >
              {isSameUser && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    zIndex: 3,
                  }}
                >
                  <IconButton onClick={() => setEditPastExperienceOpen(true)}>
                    <AddCircleOutlineOutlinedIcon />
                  </IconButton>
                </Box>
              )}
              {profileData?.previousWorkExperience?.length! > 0 ? (
                <>
                  {profileData?.previousWorkExperience?.map(
                    (eachPreviousWork) => (
                      <Stack
                        direction="column"
                        mt={2}
                        key={eachPreviousWork._id}
                        sx={{ position: "relative" }}
                      >
                        <Stack direction="row" alignItems="flex-start">
                          <Typography variant="subtitle2" fontWeight="500">
                            {eachPreviousWork.designation} •{" "}
                            {eachPreviousWork.company}
                          </Typography>

                          {isSameUser ? (
                            <Stack direction="row" sx={{ ml: 2 }}>
                              <IconButton
                                onClick={() => {
                                  setChosenExperience(eachPreviousWork);
                                  setEditPastExperienceOpen(true);
                                }}
                                sx={{ padding: "0", mr: 1 }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>

                              <IconButton
                                onClick={() =>
                                  onDeleteExperienceClick(eachPreviousWork._id)
                                }
                                sx={{ padding: "0", mr: 1 }}
                              >
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ) : null}
                        </Stack>

                        <Typography
                          variant="caption"
                          color="secondary"
                          fontWeight="400"
                          textTransform="lowercase"
                          mb={1}
                        >
                          {eachPreviousWork?.startDate
                            ? format(
                                new Date(eachPreviousWork.startDate),
                                "LLL yyyy"
                              )
                            : "n/a"}{" "}
                          -{" "}
                          {eachPreviousWork?.endDate
                            ? format(
                                new Date(eachPreviousWork.endDate),
                                "LLL yyyy"
                              )
                            : "present"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="secondary"
                          fontWeight="400"
                        >
                          {eachPreviousWork.brief}
                        </Typography>
                      </Stack>
                    )
                  )}
                </>
              ) : null}
              {editPastExperienceOpen && (
                <EditPastExperiences
                  open={editPastExperienceOpen}
                  onClose={onClose}
                  pastExperience={chosenExperience}
                />
              )}
            </ProfileCard>

            <ProfileCard
              title="tags/industries"
              isSameUser={isSameUser}
              from="tagsAndIndustry"
              tagsAndIndustry={profileData?.industryTags}
            >
              <Stack direction="row" flexWrap="wrap" mt={1}>
                {profileData?.industryTags.map((industry) => (
                  <Chip
                    key={industry}
                    label={industry}
                    variant="outlined"
                    size="small"
                    sx={{
                      fontSize: 12,
                      fontWeight: 400,
                      borderRadius: "4px",
                      mr: 1,
                      mb: 1,
                      color: "secondary",
                    }}
                  />
                ))}
              </Stack>
            </ProfileCard>

            <ProfileCard
              title="my top skill sets"
              isSameUser={isSameUser}
              from="topSkills"
              topSkills={profileData?.topSkills}
            >
              <Stack direction="row" flexWrap="wrap" mt={1}>
                {profileData?.topSkills.map((skill) => (
                  <Chip
                    key={skill}
                    label={skill}
                    variant="outlined"
                    size="small"
                    sx={{
                      fontSize: 12,
                      fontWeight: 400,
                      borderRadius: "4px",
                      mr: 1,
                      mb: 1,
                      color: "secondary",
                    }}
                  />
                ))}
              </Stack>
            </ProfileCard>

            <ProfileCard
              title=""
              isSameUser={isSameUser}
              from="education"
              underGrad={profileData?.undergrad}
              postGrad={profileData?.postgrad}
            >
              <Typography fontWeight="500" variant="subtitle1" gutterBottom>
                grad school
              </Typography>
              <Typography
                variant="subtitle2"
                color="secondary"
                fontWeight="400"
              >
                {profileData?.postgrad ? profileData?.postgrad : "n/a"}
              </Typography>

              <Typography
                fontWeight="500"
                variant="subtitle1"
                gutterBottom
                mt={1}
              >
                college
              </Typography>
              <Typography
                variant="subtitle2"
                color="secondary"
                fontWeight="400"
              >
                {profileData?.undergrad ? profileData?.undergrad : "n/a"}
              </Typography>
            </ProfileCard>

            <ProfileCard
              title="featured links"
              isSameUser={isSameUser}
              from="featuredLinks"
              featuredLinks={profileData?.featuredLinks}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="stretch"
                flexWrap="wrap"
                mt={1}
                width="100%"
              >
                {profileData?.featuredLinks?.map((link: string, index) => (
                  <Box
                    key={index}
                    sx={{
                      my: {
                        xs: 1,
                        md: 2,
                      },
                      width: "100%",
                    }}
                  >
                    <LinkPreview
                      openInNewTab
                      url={link.startsWith("http") ? link : `https://${link}`}
                      fallbackImageSrc={"/placeholder.jpeg"}
                      className={`${styles.linkPreviewText} ${styles.linkPreviewImage}`}
                      showPlaceholderIfNoImage
                      fetcher={() =>
                        customFetcher(
                          link.startsWith("http") ? link : `https://${link}`
                        )
                      }
                      fallback={
                        <Box
                          key={index}
                          sx={{
                            my: {
                              xs: 1,
                              md: 2,
                            },
                            width: "100%",
                          }}
                        >
                          <a
                            href={
                              link.startsWith("http") ? link : `https://${link}`
                            }
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              width: "100%",
                              wordBreak: "break-all",
                            }}
                          >
                            <Card
                              variant="outlined"
                              sx={{
                                px: 1,
                                py: 1,
                                cursor: "pointer",
                              }}
                            >
                              <CardMedia
                                component="img"
                                height="200"
                                image="/placeholder.jpeg"
                                alt="link preview image"
                              />
                              <CardContent
                                sx={{
                                  height: "100%",
                                  textAlign: "left",
                                  py: 2,
                                  px: 0,
                                }}
                              >
                                <Typography variant="body2" color="secondary">
                                  {link.startsWith("http")
                                    ? link
                                    : `https://${link}`}
                                </Typography>
                              </CardContent>
                            </Card>
                          </a>
                        </Box>
                      }
                    />
                  </Box>
                ))}
              </Stack>
            </ProfileCard>
          </Grid>
        </Grid>
      </Container>
    </ProtectedRoute>
  );
};

export default ProfileScreen;
